import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { NewProductFields } from 'quote-detail/new-product-fields';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { defaultProduct, discountRecommendationBrands } from 'shared/constants';
import { useQuoteState } from 'shared/contexts';
import { useExecuteModel } from 'shared/mutations';
import { Brands, MarginParamsChild, ResponseResult, ProductForm, ProductRecommendationLookup } from 'shared/types';
import { buildChildModelPayload, buildQuoteProduct } from 'shared/utility';
import { v4 as uuid } from 'uuid';

interface NewProductByRecommendationProps {
  customerId: string;
}

export function NewProductByRecommendation({ customerId }: NewProductByRecommendationProps) {
  const { brand } = useParams<{ brand: Brands }>();
  const isDiscountBasedBrand: boolean = discountRecommendationBrands.includes(brand);
  const { getValues } = useFormContext();
  const {
    newProductByRecommendationDialogOpen,
    toggleNewProductByRecommendationDialog,
    setTempProduct,
    recommendationsSelected,
    setRecommendationsSelected,
  } = useQuoteState();
  const executeMutation = useExecuteModel<MarginParamsChild, ResponseResult>('marginTarget');

  const methods = useForm<ProductForm>({
    defaultValues: defaultProduct(),
  });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    reset(defaultProduct());
  }, [newProductByRecommendationDialogOpen, reset]);

  const onClose = useCallback(() => {
    toggleNewProductByRecommendationDialog();
  }, [toggleNewProductByRecommendationDialog]);

  const buildProductToAdd = (recommendation: ProductRecommendationLookup, form: ProductForm) => {
    let productToAdd = defaultProduct();
    productToAdd = { ...form };
    productToAdd.productGuid = uuid();
    productToAdd.itemId = recommendation.itemid;
    productToAdd.name = recommendation.itemdescription;
    productToAdd.lastCostPaid = recommendation.cost;
    return productToAdd;
  };

  const onSubmit = useCallback(
    async (data: ProductForm) => {
      recommendationsSelected.forEach(async (recommendation: ProductRecommendationLookup, i: number) => {
        try {
          const productToAdd = buildProductToAdd(recommendation, data);
          const {
            customer: {
              customerNumber,
              customerTier,
              customerType,
              competitionAdjustment,
              competitiveAdjustment,
              customerNumberAdjusted,
            },
            marginFloor,
          } = getValues();

          const modelPayload = buildChildModelPayload(
            {
              customerNumber,
              customerTier,
              customerType,
              competitionAdjustment,
              competitiveAdjustment,
              customerNumberAdjusted,
            },
            productToAdd,
            brand,
            marginFloor
          );

          const calcResponse = await executeMutation.mutateAsync(modelPayload);

          const newProduct = buildQuoteProduct(productToAdd, calcResponse.data, isDiscountBasedBrand, brand);
          newProduct.calculationId = calcResponse.metadata.calculationId;
          setTempProduct(newProduct);
          if (i === recommendationsSelected.length - 1) {
            setRecommendationsSelected([]);
            onClose();
          }
        } catch (error) {
          console.error(error);
        }
      });
    },
    [
      getValues,
      brand,
      executeMutation,
      setTempProduct,
      onClose,
      isDiscountBasedBrand,
      setRecommendationsSelected,
      recommendationsSelected,
    ]
  );

  const buttonText = executeMutation.isLoading ? 'Calculating Prices...' : 'Add to Quote';
  return (
    <Dialog open={newProductByRecommendationDialogOpen} onClose={onClose}>
      <DialogTitle>Add Products to Quote</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form aria-label='form' id='new-product-by-recommendation-form' onSubmit={handleSubmit(onSubmit)}>
            <NewProductFields customerId={customerId} isRecommendation />
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          type='submit'
          disabled={executeMutation.isLoading}
          form='new-product-by-recommendation-form'
          color='secondary'
          variant='contained'
        >
          {buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
